import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { subWeeks, startOfWeek, format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";

export interface ExecutiveWeeklyRow {
  week_start: string;
  runs_published: number;
  merchants_scanned: number;
  merchants_with_deals_found: number;
  merchants_with_published_deals: number;
  total_scan_units: number;
  total_candidates_found: number;
  total_deals_found: number;
  total_deals_published: number;
  deals_found_per_scan_unit: number;
  deals_published_per_scan_unit: number;
  zero_deal_merchant_rate: number;
}

interface MerchantWeeklyRow {
  merchant_source_id: string;
  merchant_name: string | null;
  batch_week_start: string;
  scans_completed: number;
  total_scan_units: number;
  total_pages_scanned: number;
  total_candidate_pages: number;
  total_deals_found: number;
  total_deals_published: number;
  avg_deal_per_scan_ratio: number;
  zero_deal_week_rate: number;
}

export interface ExecutiveMerchantRollup {
  merchant_source_id: string;
  merchant_name: string;
  scans_completed: number;
  total_scan_units: number;
  total_deals_found: number;
  total_deals_published: number;
  avg_deal_per_scan_ratio: number;
  zero_deal_rate: number;
  weeks_seen: number;
}

export interface ExecutiveKpiSummary {
  dealsPublished: number;
  dealsFound: number;
  merchantsScanned: number;
  merchantsWithDealsFound: number;
  dealsFoundPerScanUnit: number;
  zeroDealMerchantRate: number;
}

function toNumeric(value: unknown): number {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

export function useExecutiveSummaryDashboard(windowWeeks: 4 | 8 | 12) {
  const windowStart = useMemo(() => {
    const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
    return format(subWeeks(weekStart, windowWeeks - 1), "yyyy-MM-dd");
  }, [windowWeeks]);

  const weeklyQuery = useQuery({
    queryKey: ["exec-summary", "weekly", windowWeeks],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_exec_summary_weekly" as any)
        .select("*")
        .gte("week_start", windowStart)
        .order("week_start", { ascending: true });

      if (error) throw error;
      return (data ?? []) as unknown as ExecutiveWeeklyRow[];
    },
    staleTime: 30_000,
  });

  const merchantQuery = useQuery({
    queryKey: ["exec-summary", "merchant", windowWeeks],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_merchant_scan_yield_weekly" as any)
        .select("merchant_source_id, merchant_name, batch_week_start, scans_completed, total_scan_units, total_pages_scanned, total_candidate_pages, total_deals_found, total_deals_published, avg_deal_per_scan_ratio, zero_deal_week_rate")
        .gte("batch_week_start", windowStart)
        .order("batch_week_start", { ascending: false });

      if (error) throw error;

      const rows = (data ?? []) as unknown as MerchantWeeklyRow[];
      const byMerchant = new Map<string, ExecutiveMerchantRollup>();
      const zeroRateSum = new Map<string, number>();

      for (const row of rows) {
        const key = row.merchant_source_id;
        const existing = byMerchant.get(key) ?? {
          merchant_source_id: row.merchant_source_id,
          merchant_name: row.merchant_name || row.merchant_source_id,
          scans_completed: 0,
          total_scan_units: 0,
          total_deals_found: 0,
          total_deals_published: 0,
          avg_deal_per_scan_ratio: 0,
          zero_deal_rate: 0,
          weeks_seen: 0,
        };

        existing.scans_completed += toNumeric(row.scans_completed);
        existing.total_scan_units += toNumeric(row.total_scan_units);
        existing.total_deals_found += toNumeric(row.total_deals_found);
        existing.total_deals_published += toNumeric(row.total_deals_published);
        existing.weeks_seen += 1;
        zeroRateSum.set(key, (zeroRateSum.get(key) ?? 0) + toNumeric(row.zero_deal_week_rate));

        byMerchant.set(key, existing);
      }

      const aggregated = Array.from(byMerchant.values()).map((row) => ({
        ...row,
        avg_deal_per_scan_ratio: row.total_scan_units > 0
          ? row.total_deals_found / row.total_scan_units
          : 0,
        zero_deal_rate: row.weeks_seen > 0
          ? Math.max(0, Math.min(1, (zeroRateSum.get(row.merchant_source_id) ?? 0) / row.weeks_seen))
          : 0,
      }));

      aggregated.sort((a, b) => {
        if (b.total_scan_units !== a.total_scan_units) {
          return b.total_scan_units - a.total_scan_units;
        }
        if (a.avg_deal_per_scan_ratio !== b.avg_deal_per_scan_ratio) {
          return a.avg_deal_per_scan_ratio - b.avg_deal_per_scan_ratio;
        }
        return b.total_deals_found - a.total_deals_found;
      });

      return aggregated;
    },
    staleTime: 30_000,
  });

  const kpis = useMemo<ExecutiveKpiSummary>(() => {
    const weeklyRows = weeklyQuery.data ?? [];
    let dealsPublished = 0;
    let dealsFound = 0;
    let merchantsScanned = 0;
    let merchantsWithDealsFound = 0;
    let totalScanUnits = 0;
    let zeroRateWeightedSum = 0;
    let zeroRateWeight = 0;

    for (const row of weeklyRows) {
      dealsPublished += toNumeric(row.total_deals_published);
      dealsFound += toNumeric(row.total_deals_found);
      merchantsScanned += toNumeric(row.merchants_scanned);
      merchantsWithDealsFound += toNumeric(row.merchants_with_deals_found);
      const weeklyMerchants = toNumeric(row.merchants_scanned);
      totalScanUnits += toNumeric(row.total_scan_units);
      zeroRateWeightedSum += toNumeric(row.zero_deal_merchant_rate) * weeklyMerchants;
      zeroRateWeight += weeklyMerchants;
    }

    return {
      dealsPublished,
      dealsFound,
      merchantsScanned,
      merchantsWithDealsFound,
      dealsFoundPerScanUnit: totalScanUnits > 0 ? dealsFound / totalScanUnits : 0,
      zeroDealMerchantRate: zeroRateWeight > 0 ? zeroRateWeightedSum / zeroRateWeight : 0,
    };
  }, [weeklyQuery.data]);

  return {
    windowStart,
    weeklyData: weeklyQuery.data ?? [],
    merchantData: merchantQuery.data ?? [],
    kpis,
    isLoading: weeklyQuery.isLoading || merchantQuery.isLoading,
    isError: weeklyQuery.isError || merchantQuery.isError,
    error: weeklyQuery.error ?? merchantQuery.error,
    refetch: async () => {
      await Promise.all([weeklyQuery.refetch(), merchantQuery.refetch()]);
    },
  };
}
